/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect } from "react";
import { ChartType } from "~/generated/graphql";
import {
  DateRange,
  EstatePeriod,
  Granularity,
  ChartType as CostAnalysisChartType,
} from "~/components";
import {
  useEstateRecordsGroupByChartJob,
  EstateRecordsGroupingChart,
  EstateGroupBy,
  TotalCostTable,
  EstateRecordsGroupingTable,
  useEstateRecordsCost,
} from "~/views/estate/components/Charts";
import { ENV_TYPE_SORT_OPTIONS } from "./components";
import { isTableDataSortable, sortDataByTotal } from "./helpers";

type Props = {
  period: EstatePeriod;
  granularity: Granularity;
  range: DateRange | undefined;
  setIsDataLoading: (isLoading: boolean) => void;
};

export function EstateRecordsEnvironmentTypeView({
  period,
  granularity,
  range,
  setIsDataLoading,
}: Props) {
  const {
    startDate,
    endDate,
    isDataLoading,
    isChartDataLoading,
    isTableDataLoading,
    chartData,
    tableData,
    tableError,
    sortField,
    sortDirection,
    onSortSelect,
    onSortOrderChange,
  } = useEstateRecordsGroupByChartJob({
    chartType: ChartType.EnvironmentType,
  });

  useEffect(() => {
    setIsDataLoading(isDataLoading);
  }, [isDataLoading, setIsDataLoading]);

  const {
    costPerGroupChartData,
    costPerGroupTableData,
    tableCount,
    extraDetails,
    allColumns,
    stackBarColors,
    chartKeys,
    firstColumnWidth,
  } = useEstateRecordsCost({
    chartData,
    tableData,
    granularity,
    period,
    startDate,
    endDate,
    chartType: CostAnalysisChartType.COST_PER_ENVIRONMENT_TYPE,
  });

  const sortedCostPerEnvTypeTableData = isTableDataSortable(sortField, period)
    ? sortDataByTotal(costPerGroupTableData, sortDirection)
    : costPerGroupTableData;

  return (
    <>
      <EstateRecordsGroupingChart
        data={costPerGroupChartData}
        granularity={granularity}
        loading={isChartDataLoading}
        startDate={startDate}
        endDate={endDate}
        period={period}
        colors={stackBarColors}
        keys={chartKeys}
        chartType={CostAnalysisChartType.COST_PER_ENVIRONMENT_TYPE}
      />
      <TotalCostTable
        period={period}
        range={range}
        count={tableData?.chartJobTable?.data?.count ?? 0}
        isLoading={isTableDataLoading}
        chartType={EstateGroupBy.ENV}
        totalTop={extraDetails?.totalTop ?? "-"}
        totalOther={extraDetails?.totalOther ?? "-"}
      />
      <EstateRecordsGroupingTable
        loading={isTableDataLoading}
        sortField={sortField}
        sortDirection={sortDirection}
        sortOptions={ENV_TYPE_SORT_OPTIONS}
        tableCount={tableCount ?? 0}
        columns={allColumns}
        data={sortedCostPerEnvTypeTableData}
        firstColumnWidth={firstColumnWidth}
        error={tableError}
        period={period}
        onSortSelect={onSortSelect}
        onSortDirectionSelect={onSortOrderChange}
      />
    </>
  );
}
